import React, { useEffect, useMemo, useState } from 'react';
import usePageTitle from '../../../hooks/usePageTitle';
import { BaseQueryParams } from '../../../providers/Global.model';
import TwTable, { TwTableColumn, TwTableData } from '../../../components/TwTable/TwTable';
import TwPaginator from '../../../components/TwPaginator/TwPaginator';
import TWGenericMenu, {
  TwGenericMenuOptions
} from '../../../components/TwGenericMenu/TwGenericMenu';
import TwDialog from '../../../components/TwDialog/TwDialog';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import { ReactComponent as WarningSign } from '../../../assets/warning-sign.svg';
import Box from '@mui/material/Box/Box';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import StringUtils from '../../../utils/stringUtils';
import TwBackdrop from '../../../components/TwBackdrop/TwBackdrop';
import { UserResponse, USER_ROLE, SortSettings } from '../../../providers/User/User.model';
import UserService from '../../../providers/User/User.service';
import { Characters, useUnformatValue } from '../../../hooks/useAllowCharacters';
import useAuth from '../../../providers/Auth/Auth.provider';
import { CarrierPayRateType, CarrierResponse } from '../../../redux/api/carriers/carriers.model';
import {
  useDeleteCarrierMutation,
  useGetCarriersQuery
} from '../../../redux/api/carriers/carrierApi';
import { logRocketTrack } from '../../../utils/logRocketUtils';
import './Carriers.scss';
import TwEditCompaniesModal from '../EditCompaniesModal/TwEditCompaniesModal';
import { CompaniesEntities } from '../Companies';
import { useNavigate } from 'react-router-dom';
import TwDeleteModal from '../../../components/TwDeleteModal/TwDeleteModal';
import TwDataGrid, { elementRenderBasic } from '../../../components/TwDataGrid/TwDataGrid';
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColDef,
  GridRenderCellParams,
  GridSortModel
} from '@mui/x-data-grid-pro';
import {
  ActiveColumns,
  DEFAULT_CARRIER_COLUMNS,
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_SORT_SETTINGS
} from '../../../providers/Settings/DisplaySettings.model';
import SettingsService from '../../../providers/Settings/Settings.service';
import TwColumnsMenu from '../../../components/TwColumnsMenu/TwColumnsMenu';
import { SettingsUtils } from '../../../utils/settingsUtils';
import TwTooltip from '../../../components/TwTooltip/TwTooltip';
import TwCarrierEditNotes, { CarrierEditNotesProps } from '../../CarrierDetail/TwCarrierNoteModal';
import TwNotesPreview from '../../../components/TwNotesPreview/TwNotesPreview';

const Carriers: React.FC = (): JSX.Element => {
  usePageTitle('Carriers | Truckbase');
  const {
    updateUserDisplaySettings,
    data: { userData }
  } = useAuth();
  const navigate = useNavigate();

  const [activeColumns, setActiveColumns] = useState<ActiveColumns[]>([]);
  const [pinnedColumns, setPinnedColumns] = useState<string[]>([]);

  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pageParams, setPageParams] = useState<BaseQueryParams>({
    offset: 0,
    limit: itemsPerPage,
    deleted: false,
    includeArchived: true,
    sort_by: 'name',
    order_by: 'asc'
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: SettingsUtils.transformCarrierSortBySettings(
        userData?.userDisplaySettings?.sort_settings?.CARRIERS?.sort_by ||
          DEFAULT_SORT_SETTINGS.CARRIERS.sort_by
      ),
      sort:
        userData?.userDisplaySettings?.sort_settings?.CARRIERS?.order_by ||
        DEFAULT_SORT_SETTINGS.CARRIERS.order_by
    }
  ]);

  const [tableData, setTableData] = useState<TwTableData[]>([]);
  const [isEditCarrierOpen, setIsEditCarrierOpen] = useState<boolean>(false);
  const [selectedCarrierId, setSelectedCarrierId] = useState<string>('');
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState<boolean>(false);

  const unformatString = useUnformatValue(Characters.number);
  const { addNotification } = useNotifications();
  // RTK Carrier querys
  const {
    data: carriers = { content: [], total: 0 },
    error: carriersError,
    isLoading,
    refetch
  } = useGetCarriersQuery(pageParams);
  const [deleteCarrier] = useDeleteCarrierMutation();

  /** Changes page */
  const handlePageChange = (value: number): void => {
    setPage(value);
  };

  const saveColumnChanges = (columns: ActiveColumns[]): void => {
    SettingsService.updateUserDisplaySettings({
      ...userData?.userDisplaySettings,
      carrier_table: {
        columns: columns
      }
    })
      .then((res: any) => {
        updateUserDisplaySettings(res.data.userDisplaySettings);
        setActiveColumns(columns);
      })
      .catch((err) => {
        addNotification({ message: 'Error saving columns changes', variant: 'error' });
      });
  };

  const handleResetColumns = (): void => {
    setActiveColumns(DEFAULT_CARRIER_COLUMNS);
    saveColumnChanges(DEFAULT_CARRIER_COLUMNS);
  };

  const formatColumns: GridColDef[] = useMemo(() => {
    const activeFilteredColumns = activeColumns
      .filter((column) => column.visible)
      .map((column) => {
        const baseColumn: GridColDef = {
          field: column.name,
          headerName: column.label.toUpperCase(),
          minWidth: 100,
          headerAlign: 'left',
          align: 'left',
          sortable: true,
          renderCell: elementRenderBasic,
          sortingOrder: ['desc', 'asc']
        };

        switch (column.name) {
          case 'name':
            return {
              ...baseColumn,
              width: 100,
              maxWidth: 100,
              renderCell: elementRenderBasic,
              minWidth: 150
            };

          case 'payRate':
            return {
              ...baseColumn,
              width: 100,
              maxWidth: 100,
              renderCell: elementRenderBasic,
              minWidth: 100,
              sortable: false
            };

          case 'notes':
            return {
              ...baseColumn,
              type: 'string',
              minWidth: 200,
              maxWidth: 400,
              renderCell: (params: GridRenderCellParams): JSX.Element => {
                return (
                  <TwNotesPreview
                    value={params.value}
                    EditNoteModal={(props): JSX.Element => (
                      <TwCarrierEditNotes {...props} carrier={params.row.carrier} />
                    )}
                  />
                );
              }
            };

          default:
            return baseColumn;
        }
      });

    const menuColumn: GridColDef = {
      field: 'menu',
      headerName: '',
      sortable: false,
      filterable: false,
      maxWidth: 50,
      width: 50,
      resizable: false,
      headerClassName: 'custom-header-menu-cell',
      renderCell: elementRenderBasic,
      renderHeader: () => (
        <TwColumnsMenu
          sx={{
            '& .MuiIconButton-root': {
              padding: 0,
              backgroundColor: 'transparent !important',
              '&:hover': {
                backgroundColor: 'transparent !important'
              }
            },
            '& .MuiSvgIcon-root': {
              color: 'inherit'
            }
          }}
          entity="carrier"
          activeColumns={activeColumns}
          onSaveColumnChanges={saveColumnChanges}
          onReset={handleResetColumns}
        />
      )
    };

    return [...activeFilteredColumns, menuColumn];
  }, [activeColumns]);

  const saveItemsPerPage = (value: string): void => {
    SettingsService.updateUserDisplaySettings({
      ...userData?.userDisplaySettings,
      carrier_page_size: Number(value)
    })
      .then((res: any) => {
        setItemsPerPage(Number(value));
        setPage(1);
        setPageParams({ ...pageParams, offset: 0, limit: Number(value) });
        updateUserDisplaySettings(res.data.userDisplaySettings);
      })
      .catch((err) => {
        console.error(err);
        addNotification({ message: 'Error saving items per page', variant: 'error' });
      });
  };

  const getTableSettings = (): void => {
    let columnsFromSettings: ActiveColumns[] = [];

    if (userData?.userDisplaySettings?.carrier_table) {
      columnsFromSettings = userData?.userDisplaySettings?.carrier_table.columns || [];
    }

    const pageSize = userData?.userDisplaySettings?.carrier_page_size || 50;

    if (pageSize) {
      setItemsPerPage(pageSize);
      setPageParams({ ...pageParams, offset: 0, limit: Number(pageSize) });
    }

    // Format columns from settings, add label and sort by index to show in the same order
    if (!columnsFromSettings || columnsFromSettings.length === 0) {
      columnsFromSettings = DEFAULT_CARRIER_COLUMNS;
    } else {
      columnsFromSettings = DEFAULT_CARRIER_COLUMNS.map((col) => {
        const column = columnsFromSettings.find((c) => c.name === col.name);
        return column || col;
      });
    }
    columnsFromSettings.sort((a, b) => a.index - b.index);

    setActiveColumns(columnsFromSettings);
  };

  const updateSortSettings = ({
    sort_by,
    order_by
  }: {
    sort_by: string;
    order_by: string;
  }): void => {
    if (!userData) return;

    setSortModel([
      {
        field: SettingsUtils.transformCarrierSortBySettings(sort_by),
        sort: order_by as 'asc' | 'desc'
      }
    ]);
    SettingsService.updateUserDisplaySettings({
      ...userData?.userDisplaySettings,
      sort_settings: {
        ...userData?.userDisplaySettings?.sort_settings,
        CARRIERS: {
          sort_by: sort_by,
          order_by: order_by as 'asc' | 'desc'
        }
      } as Record<'ORDERS' | 'TRIPS' | 'CARRIERS', SortSettings>
    })
      .then((res: any) => {
        updateUserDisplaySettings(res.data.userDisplaySettings);
      })
      .catch((err) => {
        console.error(err);
        addNotification({ message: 'Error updating sort settings', variant: 'error' });
      });
  };

  useEffect(() => {
    getTableSettings();
  }, [userData]);

  useEffect(() => {
    if (formatColumns.length > 0) {
      setPinnedColumns([
        GRID_DETAIL_PANEL_TOGGLE_COL_DEF.field,
        GRID_CHECKBOX_SELECTION_COL_DEF.field,
        formatColumns[0].field
      ]);
    }
  }, [formatColumns]);

  const handleCreateUserOrResendInvite = async (
    carrier: CarrierResponse
  ): Promise<UserResponse | void> => {
    if (!carrier.email_address) {
      logRocketTrack('Team Management', {
        action: 'Create carrier',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Please enter email address',
        variant: 'error'
      });
      return Promise.reject();
    }
    if (carrier.first_name === null || carrier.last_name === null) {
      logRocketTrack('Team Management', {
        action: 'Create carrier',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Please enter point of contact first and last name',
        variant: 'error'
      });
      return Promise.reject();
    }

    if (carrier.twuser_id) {
      const userInvited = await UserService.invite(carrier.twuser_id);
      addNotification({
        message: 'Invite sent',
        variant: 'success'
      });
      return userInvited;
    }
    const user = await UserService.findByEmail(carrier.email_address);
    if (!user) {
      // if carrier does not have twuser_id (user), then create a user and send invite.
      const newUser = await UserService.create({
        first_name: carrier.first_name,
        last_name: carrier.last_name,
        carrier_name: carrier.name,
        email_address: carrier.email_address,
        phone: carrier.phone ? unformatString(carrier.phone) : undefined,
        send_email_invite: true,
        role: USER_ROLE.CARRIER
      });
      addNotification({
        message: 'Invite sent',
        variant: 'success'
      });
      return newUser;
    }
    await UserService.invite(user.id);
    addNotification({
      message: 'Invite sent',
      variant: 'success'
    });
    return user;
  };

  /**
   * Menu available for each carrier
   */
  const CarrierMenu: React.FC<{ carrier: CarrierResponse }> = ({
    carrier,
    carrier: { id }
  }: any): JSX.Element => {
    const options: TwGenericMenuOptions[] = [
      {
        label: 'Edit',
        action: (): void => {
          setSelectedCarrierId(id);
          setIsEditCarrierOpen(true);
        }
      },
      {
        label: 'Delete',
        className: 'carriers__delete',
        action: (): void => {
          setSelectedCarrierId(id);
          setDeleteConfirmationOpen(true);
        }
      }
    ];

    // only adding send invite option if not inviting self
    if (userData?.email_address !== carrier.email_address) {
      options.splice(1, 0, {
        label: 'Send Invite',
        action: (): Promise<UserResponse | void> => handleCreateUserOrResendInvite(carrier),
        disabled: !carrier.email_address
      });
    }

    return (
      <div className="carriers__options">
        <TWGenericMenu options={options} />
      </div>
    );
  };

  const getPayRate = (rate: string, type: CarrierPayRateType): string => {
    if (!rate) return '';

    const cleanRate = rate.replace(/(\%|\$)/gi, '');

    if (type === 'PERCENTAGE') {
      return `${cleanRate}%`;
    } else if (type === 'PER_HOUR') {
      return `$${cleanRate}/hr`;
    } else if (type === 'PER_MILE') {
      return `$${cleanRate}/mi`;
    }

    return `$${rate}`;
  };

  const transformCarriers = (carriers: CarrierResponse[]): void => {
    const carrierList: TwTableData[] = [];
    carriers.forEach((carrier) => {
      const tableItem: TwTableData = {
        id: carrier.id,
        name: carrier.name,
        address: carrier.location?.full_address ?? '',
        emailAddress: carrier.email_address ? carrier.email_address : '',

        phone: StringUtils.formatPhoneNumber(carrier.phone ?? '', carrier.phone_ext ?? ''),
        taxId: carrier.tax_id ?? '-',
        mcNumber: carrier.mc_number ?? '-',
        externalCompanyId: carrier.external_company_id ?? '-',
        payTo: carrier.pay_to ?? '-',
        pointOfContact: `${carrier.first_name ? carrier.first_name : ''}  ${
          carrier.last_name ? carrier.last_name : ''
        }`,
        payRate: getPayRate(carrier.pay_rate, carrier.default_pay_rate_type),
        menu: <CarrierMenu carrier={carrier} />,
        notes: carrier.notes ?? '',
        handleClick: {
          onClick: () => navigate(`/carriers/${carrier.id}`),
          ignore: ['menu']
        },
        carrier: carrier
      };
      carrierList.push(tableItem);
    });
    setTableData(carrierList);
  };

  const handleEditCarrierClose = (): void => setIsEditCarrierOpen(false);

  /** Closes Delete confirmation dialog */
  const handleConfirmationClose = (): void => {
    setDeleteConfirmationOpen(false);
  };

  const handleDeleteCarrier = async (carrierId: string): Promise<void> => {
    try {
      deleteCarrier(carrierId)
        .unwrap()
        .then((response) => {
          setDeleteConfirmationOpen(false);
          addNotification({
            message: 'Carrier deleted successfully',
            variant: 'success'
          });
        });
    } catch (e: any) {
      console.error(e);
      logRocketTrack('Carrier Management', {
        action: e.message,
        severity: 'medium',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({ message: `Error: ${e.message}`, variant: 'error' });
    }
  };

  useEffect(() => {
    const newOffset = (page - 1) * itemsPerPage;
    setPageParams({ ...pageParams, offset: newOffset, limit: itemsPerPage });
  }, [page]);

  useEffect(() => {
    transformCarriers(carriers.content);
  }, [JSON.stringify(carriers.content)]);

  useEffect(() => {
    if (carriersError) {
      logRocketTrack('Carrier Management', {
        action: 'Open Carrier table',
        severity: 'medium',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Error fetching carrier list, please try again',
        variant: 'error'
      });
    }
  }, [carriersError]);

  const handleSortModelChange = (sortModel: GridSortModel): void => {
    let sort_by = '';

    if (sortModel.length > 0) {
      switch (sortModel[0].field) {
        case 'name':
          sort_by = 'name';
          break;

        default:
          sort_by = sortModel[0].field;
          break;
      }

      if (setPageParams) {
        setPageParams({
          ...pageParams,
          sort_by: sort_by,
          order_by: sortModel[0].sort || undefined
        });
      }
      updateSortSettings({
        sort_by: sort_by,
        order_by: sortModel[0].sort || 'asc'
      });
    }
  };

  return (
    <>
      {isLoading ? (
        <TwBackdrop isOpen={isLoading} />
      ) : (
        <Box sx={{ paddingTop: '1rem' }}>
          <TwDataGrid
            columns={formatColumns}
            rows={tableData}
            hideFooter={true}
            hideFooterPagination={true}
            paginationMode="server"
            loading={isLoading}
            onRowClick={(params): void => {
              navigate(`/carriers/${params.row.id}`);
            }}
            checkboxSelection={false}
            disableRowSelectionOnClick
            pagination
            paginationModel={{ page: page - 1, pageSize: itemsPerPage }}
            rowCount={carriers.total}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            pinnedColumns={{
              left: pinnedColumns,
              right: ['menu']
            }}
          />
          <TwPaginator
            currentPage={page}
            items={carriers.total}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            itemsPerPageOptions={DEFAULT_ITEMS_PER_PAGE}
            selectItemsPerPage={saveItemsPerPage}
            sxContainer={{
              position: 'relative',
              marginBottom: '30px'
            }}
          />

          <TwEditCompaniesModal
            open={isEditCarrierOpen}
            entity={CompaniesEntities.carrier}
            entityId={selectedCarrierId}
            onClose={handleEditCarrierClose}
          />

          <TwDeleteModal
            open={deleteConfirmationOpen}
            entityName="carrier"
            onClickClose={handleConfirmationClose}
            onDelete={(): Promise<void> => handleDeleteCarrier(selectedCarrierId)}
          />
        </Box>
      )}
    </>
  );
};

export default Carriers;
